import { z } from 'zod';

import type { MessageBehavior } from './base';
import { MESSAGE_DATA_TYPES } from './discriminators';

/**
 * A content suggestion is a submitter-only, system-authored nudge hidden from
 * the expert's thread, so it must not drive the optimistic inbox preview or
 * unread badge — the expert would get a phantom unread and preview text for a
 * message they never see. The backend owns role-correct unread/preview here.
 */
export const behavior: Partial<MessageBehavior> = {
  drivesInboxPreview: false,
};

/**
 * Self-contained content card carried on a `content_suggestion` message's
 * Twilio attributes. Everything needed to render the suggestion lives here so
 * the bubble paints from attributes alone — no fetch by id on the render path.
 *
 * `contentLibraryItemId`, `title`, and `thumbnailUrl` are the display-critical
 * fields; the rest render conditionally. `matchedTopicName` is a single string
 * (one chip). `shareUrl`, `contentType`, and `matchedExcerpt` are retained for
 * analytics/accessibility but do not drive the card layout.
 *
 * `videoProvider` and `videoId` are present only when `contentType` is a video
 * post; together they let the bubble embed and play the video inline from the
 * card without a render-path fetch. They come straight from the Content Library
 * item's `videoProvider`/`videoId`. `shareUrl` is a deep link, not a video URL,
 * so it cannot substitute for these. When either is absent the card falls back
 * to linking out to the full item.
 */
export const contentCardSchema = z.object({
  contentLibraryItemId: z.number(),
  contentType: z.string().optional(),
  title: z.string(),
  subtitle: z.string().optional(),
  thumbnailUrl: z.string(),
  readTime: z.string().optional(),
  shareUrl: z.string().optional(),
  matchedTopicName: z.string().optional(),
  matchedExcerpt: z.string().optional(),
  eyebrow: z.string().optional(),
  videoProvider: z.enum(['Vimeo', 'YouTube']).optional(),
  videoId: z.string().optional(),
});

export type ContentCard = z.infer<typeof contentCardSchema>;

/**
 * Content suggestion message data — set by the backend when a Content Library
 * article is auto-matched to an inquiry. Rendered as a "While you wait" card
 * for the submitter.
 */
export const payloadSchema = z.object({
  type: z.literal(MESSAGE_DATA_TYPES.CONTENT_SUGGESTION),
  contentCard: contentCardSchema,
});

export type ContentSuggestionMessageData = z.infer<typeof payloadSchema>;

export const attributesSchema = z.object({
  /**
   * Self-contained content card for a `content_suggestion` message. Present
   * only on that message type; the bubble renders entirely from these fields.
   */
  contentCard: contentCardSchema.optional(),
});
